import React, { useContext, useEffect, useMemo, useState } from "react";
import FhirClientProvider from "./FhirClientProvider";
import Chart from "./Chart";
import "./Home.css";
import { FhirClientContext } from "../FhirClientContext";

function formatName(name = []) {
    const preferred = name.find((entry) => entry.use === "official") || name[0];
    if (!preferred) {
        return "Firstname Lastname";
    }

    const given = Array.isArray(preferred.given) ? preferred.given.join(" ") : "";
    return `${given} ${preferred.family || ""}`.trim() || "Firstname Lastname";
}

function formatRace(patient) {
    const raceExtension = (patient.extension || []).find(
        (ext) => ext.url === "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race"
    );

    const coding = (raceExtension?.extension || []).find((ext) => ext.url === "ombCategory")?.valueCoding;
    return coding?.display || coding?.code || "Not available";
}

function formatBirthplace(patient) {
    const birthplace = (patient.extension || []).find(
        (ext) => ext.url === "http://hl7.org/fhir/StructureDefinition/patient-birthPlace"
    )?.valueAddress;

    if (!birthplace) {
        return "Not available";
    }

    return [birthplace.city, birthplace.state, birthplace.country].filter(Boolean).join(", ") || "Not available";
}

function DashboardBody() {
    const { client } = useContext(FhirClientContext);
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        client.patient
            .read()
            .then((data) => {
                if (!cancelled) {
                    setPatient(data);
                    setLoading(false);
                }
            })
            .catch((readError) => {
                if (!cancelled) {
                    setError(readError);
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [client]);

    const demographics = useMemo(() => {
        if (!patient) {
            return [];
        }

        return [
            { label: "DOB", value: patient.birthDate || "Not available" },
            { label: "Race", value: formatRace(patient) },
            { label: "Gender", value: patient.gender || "Not available" },
            { label: "Birthplace", value: formatBirthplace(patient) }
        ];
    }, [patient]);

    if (loading) {
        return <p className="dashboard-message">Loading patient context...</p>;
    }

    if (error) {
        return <p className="dashboard-message dashboard-message--error">{error.message}</p>;
    }

    return (
        <main className="dashboard">
            <header className="dashboard__hero card">
                <p className="dashboard__eyebrow">FHIR SMART APP</p>
                <h1>{formatName(patient?.name)}</h1>
                <div className="demographics-strip" aria-label="Patient demographics">
                    {demographics.map((row) => (
                        <div className="demographics-pill" key={row.label}>
                            <span>{row.label}</span>
                            <strong>{row.value}</strong>
                        </div>
                    ))}
                </div>
            </header>

            <section className="dashboard__grid">
                <section className="card card--tabs">
                    <Chart patientId={patient?.id} />
                </section>
            </section>
        </main>
    );
}

/**
 * Wraps everything into `FhirClientProvider` so that any component
 * can have access to the fhir client through the context.
 */
export default function Page() {
    return (
        <FhirClientProvider>
            <DashboardBody />
        </FhirClientProvider>
    );
}
